/**
 * Cache initialization hook that integrates with authentication
 * Automatically initializes cache when user logs in and clears it on logout
 */

import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCacheStore } from '@/stores/cacheStore';
import { UserContext } from '@/stores/cacheKeys';
import { INVALIDATION_TRIGGERS } from '@/stores/cacheConfig';

export const useCacheInitialization = () => {
  const { user, isStaff, staffSession, loading } = useAuth();
  const { init, clearUserContext, invalidate, isInitialized } = useCacheStore();

  // Initialize cache when user context changes
  useEffect(() => {
    const initializeCache = async () => {
      // Don't initialize cache while auth is still loading
      if (loading) {
        return;
      }
      
      // Check if we have a valid user context
      if (!user && !staffSession) {
        // User logged out - clear cache
        if (isInitialized) {
          await clearUserContext();
        }
        return;
      }

      // Create user context for cache initialization
      const userContext: UserContext = {
        userId: user?.id || staffSession?.id || '',
        restaurantId: staffSession?.restaurant_id || '',
        isStaff: isStaff,
        allowedBranches: staffSession?.allowed_branches || []
      };

      // Initialize cache with user context
      try {
        if (!isInitialized) {
          await init(userContext);
          console.log('Cache initialized successfully');
        }
      } catch (error) {
        console.error('Failed to initialize cache:', error);
      }
    };

    initializeCache();
  }, [user, isStaff, staffSession, loading, init, clearUserContext, isInitialized]);

  return {
    isInitialized
  };
};

/**
 * Hook for handling cache invalidation events
 */
export const useCacheEventHandlers = () => {
  const { invalidate } = useCacheStore();
  const { user, isStaff, staffSession } = useAuth();

  // Handle login event
  const handleLogin = async () => {
    if (user || staffSession) {
      await invalidate(INVALIDATION_TRIGGERS.onLogin);
      console.log('Cache invalidated on login');
    }
  };

  // Handle logout event
  const handleLogout = async () => {
    await invalidate(INVALIDATION_TRIGGERS.onLogout);
    console.log('Cache invalidated on logout');
  };

  // Handle branch switch event
  const handleBranchSwitch = async (branchId?: string) => {
    await invalidate(INVALIDATION_TRIGGERS.onBranchSwitch, branchId);
    console.log('Cache invalidated on branch switch:', branchId);
  };

  // Handle order creation event
  const handleOrderCreate = async (branchId?: string) => {
    await invalidate(INVALIDATION_TRIGGERS.onOrderCreate, branchId);
    console.log('Cache invalidated on order creation:', branchId);
  };

  // Handle order status update event
  const handleOrderStatusUpdate = async (branchId?: string) => {
    await invalidate(INVALIDATION_TRIGGERS.onOrderStatusUpdate, branchId);
    console.log('Cache invalidated on order status update:', branchId);
  };

  // Handle menu item changes
  const handleMenuItemChange = async (categoryId?: string) => {
    // For menu items, we need to invalidate based on category
    // This is more complex as we need to get branch info from category
    await invalidate(INVALIDATION_TRIGGERS.onMenuItemUpdate);
    console.log('Cache invalidated on menu item change');
  };

  // Handle category changes
  const handleCategoryChange = async (branchId?: string) => {
    await invalidate(INVALIDATION_TRIGGERS.onCategoryUpdate, branchId);
    console.log('Cache invalidated on category change:', branchId);
  };

  // Handle delivery zone changes
  const handleDeliveryZoneChange = async (branchId?: string) => {
    await invalidate(INVALIDATION_TRIGGERS.onDeliveryZoneUpdate, branchId);
    console.log('Cache invalidated on delivery zone change:', branchId);
  };

  // Handle new order (real-time)
  const handleNewOrder = async (branchId?: string) => {
    await invalidate(INVALIDATION_TRIGGERS.onNewOrder, branchId);
    console.log('Cache invalidated on new order:', branchId);
  };

  // Handle table session changes
  const handleTableSessionChange = async (branchId?: string) => {
    await invalidate(INVALIDATION_TRIGGERS.onTableSessionChange, branchId);
    console.log('Cache invalidated on table session change:', branchId);
  };

  return {
    handleLogin,
    handleLogout,
    handleBranchSwitch,
    handleOrderCreate,
    handleOrderStatusUpdate,
    handleMenuItemChange,
    handleCategoryChange,
    handleDeliveryZoneChange,
    handleNewOrder,
    handleTableSessionChange
  };
};

/**
 * Hook for cache performance monitoring
 */
export const useCacheMonitoring = () => {
  const { getStats, cleanup } = useCacheStore();

  // Get current cache statistics
  const getCacheStats = async () => {
    try {
      const stats = await getStats();
      return stats;
    } catch (error) {
      console.error('Failed to get cache stats:', error);
      return null;
    }
  };

  // Trigger manual cache cleanup
  const triggerCleanup = async () => {
    try {
      await cleanup();
      console.log('Cache cleanup completed');
    } catch (error) {
      console.error('Cache cleanup failed:', error);
    }
  };

  // Log cache statistics (for development)
  const logCacheStats = async () => {
    if (import.meta.env.DEV) {
      const stats = await getCacheStats();
      if (stats) {
        console.log('Cache Statistics:', {
          totalEntries: stats.totalEntries,
          totalSize: `${(stats.totalSize / 1024).toFixed(2)} KB`,
          hitRate: `${stats.hitRate.toFixed(2)}%`,
          dataTypes: Object.entries(stats.dataTypeBreakdown).map(([type, data]) => ({
            type,
            count: data.count,
            size: `${(data.size / 1024).toFixed(2)} KB`,
            avgAge: `${(data.avgAge / 1000 / 60).toFixed(1)} min`
          }))
        });
      }
    }
  };

  return {
    getCacheStats,
    triggerCleanup,
    logCacheStats
  };
};